import React from 'react';
import { FiArrowRight, FiCompass, FiTarget, FiTrendingUp } from 'react-icons/fi';

interface NextStepsProps {
  technicalScore: number;
  communicationScore: number;
  problemSolvingScore: number;
  strength: string;
  weakness: string;
  recommendation?: string;
  onBrowseJobs: () => void;
}

type Category = 'technical' | 'communication' | 'problem_solving';

const CATEGORY_LABEL: Record<Category, string> = {
  technical: 'Teknikal',
  communication: 'Komunikasi',
  problem_solving: 'Problem Solving',
};

const TIPS: Record<Category, string[]> = {
  problem_solving: [
    'Gunakan struktur situasi, tindakan, lalu hasil yang terlihat.',
    'Tambahkan angka atau persentase untuk memperkuat cerita.',
    'Ceritakan hambatan nyata yang kamu hadapi dan cara mengatasinya.',
  ],
  technical: [
    'Perkuat pengetahuan teknis yang paling relevan dengan posisi ini.',
    'Siapkan contoh proyek atau pengalaman kerja yang bisa dijelaskan singkat.',
    'Gunakan terminologi industri dengan tepat dan tetap mudah dipahami.',
  ],
  communication: [
    'Jawab langsung ke inti pertanyaan sebelum masuk ke detail.',
    'Hindari kata ragu seperti "mungkin" atau "sepertinya" jika tidak perlu.',
    'Susun jawaban dengan alur poin utama, contoh konkret, lalu kesimpulan.',
  ],
};

const getWeakestCategory = (
  technical: number,
  communication: number,
  problemSolving: number
): Category => {
  const scores: Record<Category, number> = {
    technical,
    communication,
    problem_solving: problemSolving,
  };

  return (Object.keys(scores) as Category[]).reduce((left, right) =>
    scores[left] <= scores[right] ? left : right
  );
};

const getStrongestCategory = (
  technical: number,
  communication: number,
  problemSolving: number
): Category => {
  const scores: Record<Category, number> = {
    technical,
    communication,
    problem_solving: problemSolving,
  };

  return (Object.keys(scores) as Category[]).reduce((left, right) =>
    scores[left] >= scores[right] ? left : right
  );
};

const ScoreBar: React.FC<{ score: number }> = ({ score }) => {
  const pct = Number.isFinite(Number(score)) ? Math.max(0, Math.min(100, Math.round(Number(score)))) : 0;

  return (
    <div className="nsc__bar-wrap">
      <div
        className="nsc__bar-fill"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const NextStepsCard: React.FC<NextStepsProps> = ({
  technicalScore,
  communicationScore,
  problemSolvingScore,
  strength,
  weakness,
  recommendation,
  onBrowseJobs,
}) => {
  const weakest = getWeakestCategory(
    technicalScore,
    communicationScore,
    problemSolvingScore
  );
  const strongest = getStrongestCategory(
    technicalScore,
    communicationScore,
    problemSolvingScore
  );
  const weakestScore = {
    technical: technicalScore,
    communication: communicationScore,
    problem_solving: problemSolvingScore,
  }[weakest];

  const tips = TIPS[weakest];

  return (
    <div className="nsc">
      <div className="nsc__header">
        <div>
          <span className="nsc__eyebrow">Langkah berikutnya</span>
          <h3 className="nsc__heading">Fokus pengembangan setelah sesi ini</h3>
        </div>
      </div>

      <div className="nsc__panel nsc__panel--weak">
        <div className="nsc__panel-header">
          <span className="nsc__badge nsc__badge--weak">Perlu Ditingkatkan</span>
          <span className="nsc__category">{CATEGORY_LABEL[weakest]}</span>
          <span className="nsc__score">{weakestScore}</span>
        </div>
        <ScoreBar score={weakestScore} />
        {weakness ? <p className="nsc__ai-text">{weakness}</p> : null}
        <ul className="nsc__tips">
          {tips.map((tip, index) => (
            <li key={index} className="nsc__tip">
              <span className="nsc__tip-icon"><FiTarget /></span>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="nsc__panel nsc__panel--strong">
        <div className="nsc__panel-header">
          <span className="nsc__badge nsc__badge--strong">Kekuatanmu</span>
          <span className="nsc__category">{CATEGORY_LABEL[strongest]}</span>
        </div>
        {strength ? <p className="nsc__ai-text">{strength}</p> : null}
      </div>

      {recommendation ? (
        <div className="nsc__recommendation">
          <span className="nsc__recommendation-icon"><FiTrendingUp /></span>
          <p>{recommendation}</p>
        </div>
      ) : null}

      <div className="nsc__actions">
        <button className="nsc__btn nsc__btn--browse" onClick={onBrowseJobs}>
          <FiCompass />
          <span>Jelajahi Lowongan Lain</span>
          <FiArrowRight />
        </button>
      </div>
    </div>
  );
};

export default NextStepsCard;
